// Copyright (c) 2022 by Move Closer
import { computed, ref } from '@vue/composition-api'
import { VueConstructor } from 'vue'

import { availableRatioMap, PlayerRatio, VideoDriver, videoDriverRegistry } from './Video.config'
import { UseVideoProvides, VideoProps } from './Video.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useVideo = (props: VideoProps): UseVideoProvides => {
  /**
   * Component of driver.
   */
  const component = ref<VueConstructor | undefined>()

  /**
   * @see UseVideoProvides.hasThumbnail
   */
  const hasThumbnail = computed<boolean>(() => {
    return (typeof props.thumbnail !== 'undefined' && Object.prototype.hasOwnProperty.call(props.thumbnail, 'src'))
  })

  /**
   * @see UseVideoProvides.isVideoPlaying
   */
  const isVideoPlaying = ref<boolean>(false)

  /**
   * @see UseVideoProvides.play
   */
  const play = () => {
    isVideoPlaying.value = true
  }

  /**
   * @see UseVideoProvides.resolveDriver
   */
  const resolveDriver = (): VideoDriver | undefined => {
    for (const driver of Object.values(videoDriverRegistry)) {
      if (driver.checkForUse(props.source)) {
        return driver
      }
    }
  }

  /**
   * @see UseVideoProvides.safeRatio
   */
  const safeRatio = computed<string>(() => {
    if (typeof props.ratio === 'undefined' || !(props.ratio in availableRatioMap)) {
      return availableRatioMap[PlayerRatio['16-9']]
    }

    return availableRatioMap[props.ratio]
  })

  const videoDriver: VideoDriver | undefined = resolveDriver()

  if (typeof videoDriver !== 'undefined') {
    component.value = videoDriver.component
  } else {
    console.warn('Video: Could not find video driver for given source.')
  }

  return { component, isVideoPlaying, hasThumbnail, play, safeRatio }
}
